import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  useMediaQuery,
  useTheme,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { 
  Calendar, 
  Users, 
  Building, 
  Clock, 
  CalendarRange, 
  TrendingUp, 
  Search,
  Target,
  Package,
  Layers
} from 'lucide-react';
import api from '../services/api';
import { useRefresh } from '../contexts/RefreshContext';

// Progress bar component
const ProgressBar = ({ value }) => {
  const raw = Math.max(value, 0);
  const barValue = Math.min(raw, 100);
  const color = raw >= 100 ? 'success' : raw < 40 ? 'error' : raw < 75 ? 'warning' : 'success';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" value={barValue} color={color} sx={{ height: 8, borderRadius: 5 }} />
      </Box>
      <Box sx={{ minWidth: 48 }}>
        <Typography variant="body2" sx={{ color: raw >= 100 ? 'success.main' : 'text.secondary', fontWeight: raw >= 100 ? 600 : 400 }}>
          {`${Math.round(raw)}%`}
        </Typography>
      </Box>
    </Box>
  );
};

const ReportsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [groups, setGroups] = useState([]);
  
  // Data for reports
  const [clockLogs, setClockLogs] = useState([]);
  const [salesToday, setSalesToday] = useState([]);
  const [salesByDate, setSalesByDate] = useState([]);
  const [employeePerformance, setEmployeePerformance] = useState([]);
  const [hotelPerformance, setHotelPerformance] = useState([]);
  const [hotelDailySales, setHotelDailySales] = useState([]);
  const [performanceData, setPerformanceData] = useState({ daily_data: [], weekly_summary: [] });
  const [performanceView, setPerformanceView] = useState('employee'); // 'employee' | 'hotel'
  const [isGrouped, setIsGrouped] = useState(false);

  // Item sales data
  const [itemSalesData, setItemSalesData] = useState({
    most_sold_items: [],
    items_by_hotel: [],
    items_by_employee: []
  });
  
  // Services and selected service filter
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('all');
  
  // Filters
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedHotel, setSelectedHotel] = useState('all');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('all');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { refreshKey } = useRefresh();

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [employeesRes, hotelsRes, paymentMethodsRes, servicesRes, groupsRes] = await Promise.all([
          api.get('/employees'),
          api.get('/hotels'),
          api.get('/payment_methods'),
          api.get('/services'),
          api.get('/groups')
        ]);
        
        setEmployees(employeesRes.data.map(emp => ({ id: emp[0], name: emp[1], role_id: emp[3] })));
        
        if (Array.isArray(hotelsRes.data) && hotelsRes.data.length > 0) {
            const firstItem = hotelsRes.data[0];
            if (Array.isArray(firstItem)) {
                setHotels(hotelsRes.data.map(hotel => ({ id: hotel[0], name: hotel[1] })));
            } else if (typeof firstItem === 'object') {
                setHotels(hotelsRes.data.map(hotel => ({ id: hotel.id, name: hotel.name })));
            }
        }
        
        if (Array.isArray(paymentMethodsRes.data) && paymentMethodsRes.data.length > 0) {
            const firstItem = paymentMethodsRes.data[0];
            if (Array.isArray(firstItem)) {
                setPaymentMethods(paymentMethodsRes.data.map(pm => ({ id: pm[0], method: pm[1] })));
            } else if (typeof firstItem === 'object') {
                setPaymentMethods(paymentMethodsRes.data.map(pm => ({ id: pm.id, method: pm.method })));
            }
        }
        
        // Load services
        if (Array.isArray(servicesRes.data) && servicesRes.data.length > 0) {
            const firstItem = servicesRes.data[0];
            if (Array.isArray(firstItem)) {
                setServices(servicesRes.data.map(srv => ({ id: srv[0], name: srv[1] })));
            } else if (typeof firstItem === 'object') {
                setServices(servicesRes.data.map(srv => ({ id: srv.id, name: srv.name })));
            }
        }

        // Load groups
        if (Array.isArray(groupsRes.data) && groupsRes.data.length > 0) {
            const firstItem = groupsRes.data[0];
            if (Array.isArray(firstItem)) {
                setGroups(groupsRes.data.map(g => ({ id: g[0], name: g[1] })));
            } else if (typeof firstItem === 'object') {
                setGroups(groupsRes.data.map(g => ({ id: g.id, name: g.name })));
            }
        }
        
        await fetchDailyDashboard();
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, []);
  
  useEffect(() => {
    handleApplyFilters();
  }, [refreshKey]);

  // Fetch daily dashboard data
  const fetchDailyDashboard = async () => {
    setLoading(true);
    try {
      const [clockLogsRes, salesTodayRes] = await Promise.all([
        api.get('/clock_logs/today'),
        api.get('/sales/today')
      ]);
      setClockLogs(clockLogsRes.data);
      setSalesToday(salesTodayRes.data);
    } catch (error) {
      console.error('Error loading daily dashboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch sales data by date
  const fetchSalesByDate = async () => {
    setLoading(true);
    try {
      let paymentMethodName = 'all';
      if (selectedPaymentMethod !== 'all') {
        const selectedMethod = paymentMethods.find(pm => pm.id.toString() === selectedPaymentMethod.toString());
        if (selectedMethod) paymentMethodName = selectedMethod.method;
      }
      
      const response = await api.get('/sales/by-date', {
        params: {
          start_date: startDate,
          end_date: endDate,
          employee_id: selectedEmployee !== 'all' ? selectedEmployee : undefined,
          hotel_id: selectedHotel !== 'all' ? selectedHotel : undefined,
          payment_method: paymentMethodName !== 'all' ? paymentMethodName : undefined,
          group_id: selectedGroup !== 'all' ? selectedGroup : undefined
        }
      });
      
      const formattedData = response.data.map(item => ({
        ...item,
        displayDate: formatDisplayDate(item.date),
        count: item.count || 1
      }));
      setSalesByDate(formattedData);
    } catch (error) {
      console.error('Error fetching sales by date:', error);
    } finally {
      setLoading(false);
    }
  };
  
  function formatDisplayDate(dateStr) {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    return `${parts[2]}/${parts[1]}`;
  }

  // Fetch employee performance data
  const fetchEmployeePerformance = async () => {
    setLoading(true);
    try {
      let paymentMethodName = 'all';
      if (selectedPaymentMethod !== 'all') {
        const selectedMethod = paymentMethods.find(pm => pm.id.toString() === selectedPaymentMethod.toString());
        if (selectedMethod) paymentMethodName = selectedMethod.method;
      }
      
      const response = await api.get('/sales/by-employee', {
        params: {
          start_date: startDate,
          end_date: endDate,
          hotel_id: selectedHotel !== 'all' ? selectedHotel : undefined,
          payment_method: paymentMethodName !== 'all' ? paymentMethodName : undefined,
          group_id: selectedGroup !== 'all' ? selectedGroup : undefined
        }
      });
      
      const processedData = response.data.map(item => ({
        ...item,
        count: item.count || 1
      }));
      setEmployeePerformance(processedData);
    } catch (error) {
      console.error('Error fetching employee performance:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch hotel performance data
  const fetchHotelPerformance = async () => {
    setLoading(true);
    try {
      let paymentMethodName = 'all';
      if (selectedPaymentMethod !== 'all') {
        const selectedMethod = paymentMethods.find(pm => pm.id.toString() === selectedPaymentMethod.toString());
        if (selectedMethod) paymentMethodName = selectedMethod.method;
      }
      
      const response = await api.get('/sales/by-hotel', {
        params: {
          start_date: startDate,
          end_date: endDate,
          employee_id: selectedEmployee !== 'all' ? selectedEmployee : undefined,
          payment_method: paymentMethodName !== 'all' ? paymentMethodName : undefined,
          group_id: selectedGroup !== 'all' ? selectedGroup : undefined
        }
      });
      setHotelPerformance(response.data.summary);
      setHotelDailySales(response.data.daily);
    } catch (error) {
      console.error('Error fetching hotel performance:', error);
    } finally {
      setLoading(false);
    }
  };
  
const fetchPerformanceReport = async (useDates = false) => {
  setLoading(true);
  try {
    const params = {
      employee_id: selectedEmployee !== 'all' ? selectedEmployee : undefined,
      hotel_id: selectedHotel !== 'all' ? selectedHotel : undefined,
      group_id: selectedGroup !== 'all' ? selectedGroup : undefined,
    };

    if (useDates && startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    }

    const response = await api.get('/reports/performance', { params });
    setPerformanceData(response.data);
  } catch (error) {
    console.error('Error fetching performance report:', error);
    setPerformanceData({ daily_data: [], weekly_summary: [] });
  } finally {
    setLoading(false);
  }
};

  // Fetch item sales data
  const fetchItemSalesReport = async () => {
  setLoading(true);
  try {
    let paymentMethodName = 'all';
    if (selectedPaymentMethod !== 'all') {
      const selectedMethod = paymentMethods.find(pm => pm.id.toString() === selectedPaymentMethod.toString());
      if (selectedMethod) paymentMethodName = selectedMethod.method;
    }
    
    const response = await api.get('/reports/items', {
      params: {
        start_date: startDate,
        end_date: endDate,
        employee_id: selectedEmployee !== 'all' ? selectedEmployee : undefined,
        hotel_id: selectedHotel !== 'all' ? selectedHotel : undefined,
        payment_method: paymentMethodName !== 'all' ? paymentMethodName : undefined,
        service_id: selectedService !== 'all' ? selectedService : undefined,
        group_id: selectedGroup !== 'all' ? selectedGroup : undefined
      }
    });
    setItemSalesData(response.data);
    setIsGrouped(response.data.is_grouped || false);
  } catch (error) {
    console.error('Error fetching item sales report:', error);
    setItemSalesData({ most_sold_items: [], items_by_hotel: [], items_by_employee: [] });
    setIsGrouped(false);
  } finally {
    setLoading(false);
  }
};

const handleApplyFilters = () => {
  if (tabValue === 0) fetchDailyDashboard();
  else if (tabValue === 1) fetchSalesByDate();
  else if (tabValue === 2) fetchEmployeePerformance();
  else if (tabValue === 3) fetchHotelPerformance();
  else if (tabValue === 4) fetchPerformanceReport(true);
  else if (tabValue === 5) fetchItemSalesReport();
};

  
 const handleTabChange = (event, newValue) => {
  setTabValue(newValue);
  if (newValue === 0) fetchDailyDashboard();
  else if (newValue === 1) fetchSalesByDate();
  else if (newValue === 2) fetchEmployeePerformance();
  else if (newValue === 3) fetchHotelPerformance();
  else if (newValue === 4) fetchPerformanceReport(true);
  else if (newValue === 5) fetchItemSalesReport();
};

  
const renderFilters = () => {
  if (tabValue === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Grid container spacing={2} alignItems="center">
        {/* Date range – all tabs */}
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>

        {/* Group filter – all tabs except Daily Dashboard (0) */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth>
            <InputLabel>Group</InputLabel>
            <Select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              label="Group"
            >
              <MenuItem value="all">All Groups</MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Payment Method – only tab 1 */}
        {tabValue === 1 && (
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={selectedPaymentMethod}
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                label="Payment Method"
              >
                <MenuItem value="all">All</MenuItem>
                {paymentMethods.map((pm) => (
                  <MenuItem key={pm.id} value={pm.id}>
                    {pm.method}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Employee – disabled on tab 2 (By Employee) */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth>
            <InputLabel>Employee</InputLabel>
            <Select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              label="Employee"
              disabled={tabValue === 2}
            >
              <MenuItem value="all">All</MenuItem>
              {employees.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>
                  {employee.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Resort – disabled on tab 3 (By Hotel) */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth>
            <InputLabel>Resort</InputLabel>
            <Select
              value={selectedHotel}
              onChange={(e) => setSelectedHotel(e.target.value)}
              label="Resort"
              disabled={tabValue === 3}
            >
              <MenuItem value="all">All</MenuItem>
              {hotels.map((hotel) => (
                <MenuItem key={hotel.id} value={hotel.id}>
                  {hotel.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Service – only tab 5 (Items) */}
        {tabValue === 5 && (
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Service</InputLabel>
              <Select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                label="Service"
              >
                <MenuItem value="all">All Services</MenuItem>
                {services.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* View toggle – only tab 4 (Performance) */}
        {tabValue === 4 && (
          <Grid item xs={12} sm={6} md={2}>
            <ToggleButtonGroup
              value={performanceView}
              exclusive
              onChange={(_, v) => { if (v) setPerformanceView(v); }}
              size="small"
              fullWidth
              sx={{ height: '56px' }}
            >
              <ToggleButton value="employee" sx={{ textTransform: 'none', flex: 1 }}>
                By Employee
              </ToggleButton>
              <ToggleButton value="hotel" sx={{ textTransform: 'none', flex: 1 }}>
                By Hotel
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        )}

        {/* Apply button */}
        <Grid item xs={12} sm={12} md={2}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleApplyFilters}
            startIcon={<Search />}
            sx={{ height: '56px' }}
          >
            Apply
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};


  
  const renderDailyDashboard = () => {
    const totalSales = salesToday.reduce((sum, sale) => sum + sale.total, 0);
    const totalItems = salesToday.reduce((sum, sale) => sum + sale.items, 0);
    const totalNumberOfSales = salesToday.reduce((sum, sale) => sum + sale.sales_count, 0);
    const activeEmployees = clockLogs.filter(log => log.clock_in !== '--:--').length;
    const avgSale = totalNumberOfSales > 0 ? (totalSales / totalNumberOfSales).toFixed(2) : '0.00';
    const salesByHotel = salesToday.reduce((acc, sale) => {
      if (!acc[sale.hotel]) {
        acc[sale.hotel] = 0;
      }
      acc[sale.hotel] += sale.total;
      return acc;
    }, {});
    
    return (
      <Box>
        <Grid container spacing={1} sx={{ width: '100%', mx: 'auto' }}>
          <Grid item xs={12} sx={{ width: '100%', mx: 'auto' }}>
            <Card sx={{ width: '100%', mx: 'auto', borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
              <CardHeader title="Today's Summary" avatar={<CalendarRange size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
              <Divider />
              <CardContent>
                <Grid container spacing={5}>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Total Sales</Typography><Typography variant="h4">${totalSales.toFixed(2)}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Items Sold</Typography><Typography variant="h4">{totalItems}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Active Employees</Typography><Typography variant="h4">{activeEmployees}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Average per Sale</Typography><Typography variant="h4">${avgSale}</Typography></Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', mx: 'auto' }}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
              <CardHeader title="Time Clock Records" avatar={<Clock size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
              <Divider />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell><strong>Employee</strong></TableCell><TableCell><strong>Hotel</strong></TableCell><TableCell align="right"><strong>Clock In</strong></TableCell><TableCell align="right"><strong>Clock Out</strong></TableCell></TableRow></TableHead>
                    <TableBody>
                      {clockLogs.map((log, index) => (<TableRow key={index}><TableCell>{log.employee_name}</TableCell><TableCell>{log.hotel}</TableCell><TableCell align="right">{log.clock_in}</TableCell><TableCell align="right">{log.clock_out}</TableCell></TableRow>))}
                      {clockLogs.length === 0 && (<TableRow><TableCell colSpan={4} align="center">No time clock records today</TableCell></TableRow>)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', mx: 'auto' }}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
              <CardHeader title="Sales by Hotel" avatar={<Building size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
              <Divider />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell><strong>Hotel</strong></TableCell><TableCell align="right"><strong>Sales</strong></TableCell><TableCell align="right"><strong>% of Total</strong></TableCell></TableRow></TableHead>
                    <TableBody>
                      {Object.entries(salesByHotel).map(([hotel, value]) => (<TableRow key={hotel}><TableCell>{hotel}</TableCell><TableCell align="right">${value.toFixed(2)}</TableCell><TableCell align="right">{totalSales > 0 ? ((value / totalSales) * 100).toFixed(1) : 0}%</TableCell></TableRow>))}
                      {Object.entries(salesByHotel).length === 0 && (<TableRow><TableCell colSpan={3} align="center">No sales today</TableCell></TableRow>)}
                    </TableBody>
                    <TableFooter><TableRow sx={{ bgcolor: '#f5f5f5', borderTop: '2px solid #e0e0e0', boxShadow: '0px -2px 6px rgba(0,0,0,0.1)' }}><TableCell sx={{ fontWeight: 'bold' }}><strong>Total</strong></TableCell><TableCell align="right"><strong>${totalSales.toFixed(2)}</strong></TableCell><TableCell align="right"><strong>100%</strong></TableCell></TableRow></TableFooter>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', mx: 'auto' }}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
              <CardHeader title="Sales by Employee" avatar={<Users size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
              <Divider />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell><strong>Employee</strong></TableCell><TableCell><strong>Hotel</strong></TableCell><TableCell align="right"><strong>Items</strong></TableCell><TableCell align="right"><strong>Total</strong></TableCell></TableRow></TableHead>
                    <TableBody>
                      {salesToday.map((sale, index) => (<TableRow key={index}><TableCell>{sale.employee_name}</TableCell><TableCell>{sale.hotel}</TableCell><TableCell align="right">{sale.items}</TableCell><TableCell align="right">${sale.total.toFixed(2)}</TableCell></TableRow>))}
                      {salesToday.length === 0 && (<TableRow><TableCell colSpan={4} align="center">No sales today</TableCell></TableRow>)}
                    </TableBody>
                    <TableFooter><TableRow sx={{ bgcolor: '#f5f5f5', borderTop: '2px solid #e0e0e0', boxShadow: '0px -2px 6px rgba(0,0,0,0.1)' }}><TableCell colSpan={2}><strong>Total</strong></TableCell><TableCell align="right"><strong>{totalItems}</strong></TableCell><TableCell align="right"><strong>${totalSales.toFixed(2)}</strong></TableCell></TableRow></TableFooter>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };
  
  const renderSalesByDate = () => {
    const totalSales = salesByDate.reduce((sum, item) => sum + item.total, 0);
    const totalItems = salesByDate.reduce((sum, item) => sum + item.items, 0);
    const totalCount = salesByDate.reduce((sum, item) => sum + item.count, 0);
    const avgSale = totalCount > 0 ? (totalSales / totalCount).toFixed(2) : '0.00';
    return (
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
        <CardHeader title="Sales by Date" avatar={<Calendar size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
        <Divider />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="right"><strong>Sales</strong></TableCell>
                  <TableCell align="right"><strong>Items</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>Average</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salesByDate.map((item) => (
                  <TableRow key={item.date}>
                    <TableCell>{item.displayDate}</TableCell>
                    <TableCell align="right">{item.count}</TableCell>
                    <TableCell align="right">{item.items}</TableCell>
                    <TableCell align="right">${item.total.toFixed(2)}</TableCell>
                    <TableCell align="right">${(item.total / Math.max(item.count, 1)).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {salesByDate.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No sales in the selected period</TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ bgcolor: '#f5f5f5', borderTop: '2px solid #e0e0e0', boxShadow: '0px -2px 6px rgba(0,0,0,0.1)' }}>
                  <TableCell><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>{totalCount}</strong></TableCell>
                  <TableCell align="right"><strong>{totalItems}</strong></TableCell>
                  <TableCell align="right"><strong>${totalSales.toFixed(2)}</strong></TableCell>
                  <TableCell align="right"><strong>${avgSale}</strong></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };
  
  const renderEmployeePerformance = () => {
    const totalSales = employeePerformance.reduce((sum, item) => sum + item.sales, 0);
    const totalItems = employeePerformance.reduce((sum, item) => sum + item.items, 0);
    const totalCommission = employeePerformance.reduce((sum, item) => sum + item.commission, 0);
    const totalCount = employeePerformance.reduce((sum, item) => sum + item.count, 0);
    const avgSale = totalCount > 0 ? (totalSales / totalCount).toFixed(2) : '0.00';
    return (
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
        <CardHeader title="Employee Performance" avatar={<Users size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
        <Divider />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell align="right"><strong>Sales</strong></TableCell>
                  <TableCell align="right"><strong>Items</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>Average</strong></TableCell>
                  <TableCell align="right"><strong>Commission</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employeePerformance.map((item) => (
                  <TableRow key={item.name}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell align="right">{item.count}</TableCell>
                    <TableCell align="right">{item.items}</TableCell>
                    <TableCell align="right">${item.sales.toFixed(2)}</TableCell>
                    <TableCell align="right">${(item.sales / Math.max(item.count, 1)).toFixed(2)}</TableCell>
                    <TableCell align="right">${item.commission.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {employeePerformance.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No sales in the selected period</TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ bgcolor: '#f5f5f5', borderTop: '2px solid #e0e0e0', boxShadow: '0px -2px 6px rgba(0,0,0,0.1)' }}>
                  <TableCell><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>{totalCount}</strong></TableCell>
                  <TableCell align="right"><strong>{totalItems}</strong></TableCell>
                  <TableCell align="right"><strong>${totalSales.toFixed(2)}</strong></TableCell>
                  <TableCell align="right"><strong>${avgSale}</strong></TableCell>
                  <TableCell align="right"><strong>${totalCommission.toFixed(2)}</strong></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };
  
  const renderHotelPerformance = () => {
    const totalSales = hotelPerformance.reduce((sum, item) => sum + item.sales, 0);
    const totalItems = hotelPerformance.reduce((sum, item) => sum + item.items, 0);
    return (
      <Box>
        <Card sx={{ mb: 1, borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
          <CardHeader title="Hotel Performance" avatar={<Building size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
          <Divider />
          <CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Hotel</strong></TableCell>
                    <TableCell align="right"><strong>Employees</strong></TableCell>
                    <TableCell align="right"><strong>Items</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell>
                    <TableCell align="right"><strong>% of Total</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hotelPerformance.map((item) => (
                    <TableRow key={item.name}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">{item.employees}</TableCell>
                      <TableCell align="right">{item.items}</TableCell>
                      <TableCell align="right">${item.sales.toFixed(2)}</TableCell>
                      <TableCell align="right">{totalSales > 0 ? ((item.sales / totalSales) * 100).toFixed(1) : 0}%</TableCell>
                    </TableRow>
                  ))}
                  {hotelPerformance.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No sales in the selected period</TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ bgcolor: '#f5f5f5', borderTop: '2px solid #e0e0e0', boxShadow: '0px -2px 6px rgba(0,0,0,0.1)' }}>
                    <TableCell><strong>Total</strong></TableCell>
                    <TableCell align="right"></TableCell>
                    <TableCell align="right"><strong>{totalItems}</strong></TableCell>
                    <TableCell align="right"><strong>${totalSales.toFixed(2)}</strong></TableCell>
                    <TableCell align="right"><strong>100%</strong></TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
          <CardHeader title="Daily Sales by Hotel" avatar={<TrendingUp size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
          <Divider />
          <CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    {hotelPerformance.map((hotel) => (
                      <TableCell sx={{ fontWeight: 'bold' }} key={hotel.name} align="right">{hotel.name}</TableCell>
                    ))}
                    <TableCell align="right"><strong>Total</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hotelDailySales.map((day) => {
                    const dayTotal = Object.entries(day).filter(([key]) => key !== 'date').reduce((sum, [, value]) => sum + value, 0);
                    return (
                      <TableRow key={day.date}>
                        <TableCell>{formatDisplayDate(day.date)}</TableCell>
                        {hotelPerformance.map((hotel) => (
                          <TableCell key={hotel.name} align="right">${(day[hotel.name] || 0).toFixed(2)}</TableCell>
                        ))}
                        <TableCell align="right">${dayTotal.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {hotelDailySales.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={hotelPerformance.length + 2} align="center">No sales in the selected period</TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ bgcolor: '#f5f5f5', borderTop: '2px solid #e0e0e0', boxShadow: '0px -2px 6px rgba(0,0,0,0.1)' }}>
                    <TableCell><strong>Total</strong></TableCell>
                    {hotelPerformance.map((hotel) => (
                      <TableCell key={hotel.name} align="right"><strong>${hotel.sales.toFixed(2)}</strong></TableCell>
                    ))}
                    <TableCell align="right"><strong>${totalSales.toFixed(2)}</strong></TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  };
  
  const renderPerformanceReport = () => {
    const { daily_data = [], weekly_summary = [] } = performanceData;

    // Group by exact date so two Mondays from different weeks stay separate
    const groupedByDate = daily_data.reduce((acc, item) => {
      if (!acc[item.date]) acc[item.date] = [];
      acc[item.date].push(item);
      return acc;
    }, {});
    const sortedDates = Object.keys(groupedByDate).sort();

    const getDayLabel = (dateStr) => {
      const date = new Date(dateStr + 'T00:00:00');
      const dayName = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date);
      return `${dayName} (${formatDisplayDate(dateStr)})`;
    };

    // Hotel view: aggregate by hotel_name across all daily_data
    const hotelMap = daily_data.reduce((acc, item) => {
      const key = item.hotel_name || 'Unknown';
      if (!acc[key]) acc[key] = { hotel_name: key, total_sales: 0, total_goals: 0 };
      acc[key].total_sales += item.daily_sales;
      acc[key].total_goals += item.daily_goal;
      return acc;
    }, {});
    const hotelSummary = Object.values(hotelMap).sort((a, b) => b.total_sales - a.total_sales);

    // Hotel daily breakdown
    const hotelByDate = daily_data.reduce((acc, item) => {
      if (!acc[item.date]) acc[item.date] = {};
      const h = item.hotel_name || 'Unknown';
      if (!acc[item.date][h]) acc[item.date][h] = { sales: 0, goal: 0 };
      acc[item.date][h].sales += item.daily_sales;
      acc[item.date][h].goal += item.daily_goal;
      return acc;
    }, {});

    return (
      <Box>
        {/* EMPLOYEE VIEW */}
        {performanceView === 'employee' && (
          <>
            <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <CardHeader title="Performance Summary" avatar={<TrendingUp size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
              <Divider />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Employee</strong></TableCell>
                        <TableCell align="right"><strong>Total Sales</strong></TableCell>
                        <TableCell align="right"><strong>Total Goal</strong></TableCell>
                        <TableCell sx={{ minWidth: 160 }}><strong>Goal %</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {weekly_summary.map((item) => (
                        <TableRow key={item.employee_id}>
                          <TableCell>{item.employee_name}</TableCell>
                          <TableCell align="right">${item.total_sales.toFixed(2)}</TableCell>
                          <TableCell align="right">${item.total_goals.toFixed(2)}</TableCell>
                          <TableCell><ProgressBar value={item.weekly_percentage} /></TableCell>
                        </TableRow>
                      ))}
                      {weekly_summary.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">No performance data for the selected period.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <CardHeader title="Daily Details" avatar={<Calendar size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
              <Divider />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Employee</strong></TableCell>
                        <TableCell><strong>Hotel</strong></TableCell>
                        <TableCell align="right"><strong>Sales</strong></TableCell>
                        <TableCell align="right"><strong>Goal</strong></TableCell>
                        <TableCell sx={{ minWidth: 160 }}><strong>%</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {daily_data.length > 0 ? (
                        sortedDates.map(dateStr => (
                          <React.Fragment key={dateStr}>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>
                                {getDayLabel(dateStr)}
                              </TableCell>
                            </TableRow>
                            {groupedByDate[dateStr].map((item, index) => (
                              <TableRow key={`${dateStr}-${item.employee_id}-${index}`}>
                                <TableCell>{item.employee_name}</TableCell>
                                <TableCell>{item.hotel_name}</TableCell>
                                <TableCell align="right">${item.daily_sales.toFixed(2)}</TableCell>
                                <TableCell align="right">${item.daily_goal.toFixed(2)}</TableCell>
                                <TableCell><ProgressBar value={item.percentage} /></TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">No daily data for the selected period.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}

        {/* HOTEL VIEW */}
        {performanceView === 'hotel' && (
          <>
            <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <CardHeader title="Performance by Hotel" avatar={<Building size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
              <Divider />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Hotel</strong></TableCell>
                        <TableCell align="right"><strong>Total Sales</strong></TableCell>
                        <TableCell align="right"><strong>Total Goal</strong></TableCell>
                        <TableCell sx={{ minWidth: 160 }}><strong>Goal %</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {hotelSummary.map((h) => {
                        const pct = h.total_goals > 0 ? (h.total_sales / h.total_goals) * 100 : 0;
                        return (
                          <TableRow key={h.hotel_name}>
                            <TableCell>{h.hotel_name}</TableCell>
                            <TableCell align="right">${h.total_sales.toFixed(2)}</TableCell>
                            <TableCell align="right">${h.total_goals.toFixed(2)}</TableCell>
                            <TableCell><ProgressBar value={pct} /></TableCell>
                          </TableRow>
                        );
                      })}
                      {hotelSummary.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">No performance data for the selected period.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <CardHeader title="Daily Details by Hotel" avatar={<Calendar size={20} />} sx={{ p: 1, bgcolor: '#f5f5f5' }} />
              <Divider />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Hotel</strong></TableCell>
                        <TableCell align="right"><strong>Sales</strong></TableCell>
                        <TableCell align="right"><strong>Goal</strong></TableCell>
                        <TableCell sx={{ minWidth: 160 }}><strong>%</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedDates.length > 0 ? (
                        sortedDates.map(dateStr => (
                          <React.Fragment key={dateStr}>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                                {getDayLabel(dateStr)}
                              </TableCell>
                            </TableRow>
                            {Object.entries(hotelByDate[dateStr] || {})
                              .sort((a, b) => b[1].sales - a[1].sales)
                              .map(([hotelName, data]) => {
                                const pct = data.goal > 0 ? (data.sales / data.goal) * 100 : 0;
                                return (
                                  <TableRow key={`${dateStr}-${hotelName}`}>
                                    <TableCell>{hotelName}</TableCell>
                                    <TableCell align="right">${data.sales.toFixed(2)}</TableCell>
                                    <TableCell align="right">${data.goal.toFixed(2)}</TableCell>
                                    <TableCell><ProgressBar value={pct} /></TableCell>
                                  </TableRow>
                                );
                              })}
                          </React.Fragment>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">No daily data for the selected period.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    );
  };

  const renderItemSalesReport = () => {
  const { most_sold_items = [], items_by_hotel = [], items_by_employee = [] } = itemSalesData;

  const hotelGroups = items_by_hotel.reduce((acc, item) => {
    if (!acc[item.hotel_name]) {
      acc[item.hotel_name] = [];
    }
    acc[item.hotel_name].push(item);
    return acc;
  }, {});

  const employeeGroups = items_by_employee.reduce((acc, item) => {
    if (!acc[item.employee_name]) {
      acc[item.employee_name] = [];
    }
    acc[item.employee_name].push(item);
    return acc;
  }, {});

  return (
    <Box>
      {/* Most Sold Items Overall */}
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
        <CardHeader 
          title={selectedService !== 'all' ? `Sales for: ${services.find(s => s.id.toString() === selectedService.toString())?.name || 'Selected Service'}` : "Most Sold Items"} 
          avatar={<Package size={20} />} 
          sx={{ p: 1, bgcolor: '#f5f5f5' }} 
        />
        <Divider />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Service</strong></TableCell>
                  <TableCell align="right"><strong>Quantity</strong></TableCell>
                  <TableCell align="right"><strong>Revenue</strong></TableCell>
                  <TableCell align="right"><strong>Price</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {most_sold_items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.service_name}</TableCell>
                    <TableCell align="right">{item.total_quantity}</TableCell>
                    <TableCell align="right">${item.total_revenue.toFixed(2)}</TableCell>
                    <TableCell align="right">${(item.total_revenue / item.total_quantity).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {most_sold_items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No items sold in the selected period</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Items by Hotel */}
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
        <CardHeader 
          title={selectedService !== 'all' ? "Hotels Performance for Selected Service" : "Sales by Hotel"} 
          avatar={<Building size={20} />} 
          sx={{ p: 1, bgcolor: '#f5f5f5' }} 
        />
        <Divider />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Hotel</strong></TableCell>
                  <TableCell><strong>Service</strong></TableCell>
                  <TableCell align="right"><strong>Quantity</strong></TableCell>
                  <TableCell align="right"><strong>Revenue</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(hotelGroups).map(([hotelName, hotelItems]) => (
                  <React.Fragment key={hotelName}>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.100' }}>
                        <Building size={16} /> {hotelName}
                      </TableCell>
                    </TableRow>
                    {hotelItems.map((item, index) => (
                      <TableRow key={`${hotelName}-${index}`}>
                        <TableCell sx={{ pl: 4 }}>-</TableCell>
                        <TableCell>{item.service_name}</TableCell>
                        <TableCell align="right">{item.total_quantity}</TableCell>
                        <TableCell align="right">${item.total_revenue.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
                {Object.keys(hotelGroups).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No items sold by hotel in the selected period</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Items by Employee */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
        <CardHeader 
          title={selectedService !== 'all' ? "Employee Performance for Selected Service" : "Sales by Employee"} 
          avatar={<Users size={20} />} 
          sx={{ p: 1, bgcolor: '#f5f5f5' }} 
        />
        <Divider />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Employee</strong></TableCell>
                  {!isGrouped && <TableCell><strong>Hotel</strong></TableCell>}
                  <TableCell><strong>Service</strong></TableCell>
                  <TableCell align="right"><strong>Quantity</strong></TableCell>
                  <TableCell align="right"><strong>Revenue</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(employeeGroups).map(([employeeName, employeeItems]) => (
                  <React.Fragment key={employeeName}>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell colSpan={isGrouped ? 4 : 5} sx={{ fontWeight: 'bold', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.100' }}>
                        <Users size={16} /> {employeeName}
                      </TableCell>
                    </TableRow>
                    {employeeItems.map((item, index) => (
                      <TableRow key={`${employeeName}-${index}`}>
                        <TableCell sx={{ pl: 4 }}>-</TableCell>
                        {!isGrouped && <TableCell>{item.hotel_name}</TableCell>}
                        <TableCell>{item.service_name}</TableCell>
                        <TableCell align="right">{item.total_quantity}</TableCell>
                        <TableCell align="right">${item.total_revenue.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
                {Object.keys(employeeGroups).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isGrouped ? 4 : 5} align="center">No items sold by employees in the selected period</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};
  
  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: '1200px', margin: '0 auto' }}>
      <Paper elevation={0} sx={{ mb: 2, borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.9)', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Daily Dashboard" icon={<CalendarRange />} iconPosition="start" />
          <Tab label="Sales by Date" icon={<Calendar />} iconPosition="start" />
          <Tab label="By Employee" icon={<Users />} iconPosition="start" />
          <Tab label="By Hotel" icon={<Building />} iconPosition="start" />
          <Tab label="Performance" icon={<Target />} iconPosition="start" />
          <Tab label="Items Sold" icon={<Package />} iconPosition="start" />
        </Tabs>
      </Paper>
      
      {renderFilters()}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          {tabValue === 0 && renderDailyDashboard()}
          {tabValue === 1 && renderSalesByDate()}
          {tabValue === 2 && renderEmployeePerformance()}
          {tabValue === 3 && renderHotelPerformance()}
          {tabValue === 4 && renderPerformanceReport()}
          {tabValue === 5 && renderItemSalesReport()}
        </>
      )}
    </Box>
  );
};

export default ReportsPage;